import React, { useState } from 'react';
import { useSolarMonitoring } from '../../../hooks/useSolarMonitoring';
import { AlertRule, ActiveTab, AlertRuleFormData } from '../../../types/solarMonitoring';
import { SolarMonitoringHeader } from './SolarMonitoringHeader';
import { NavigationTabs } from './NavigationTabs';
import { OverviewTab } from './OverviewTab';
import { AlertRulesTab } from './AlertRulesTab';
import { AlertHistoryTab } from './AlertHistoryTab';
import { AlertRuleModal } from './AlertRuleModal';

export const SolarMonitoring: React.FC = () => {
  const {
    alertRules,
    alertHistory,
    monitoringStatus,
    loading,
    testEmailLoading,
    createAlertRule,
    updateAlertRule,
    deleteAlertRule,
    toggleAlertRule,
    testAlertEmail
  } = useSolarMonitoring();

  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);

  const handleCreateRule = () => {
    setEditingRule(null);
    setShowRuleModal(true);
  };

  const handleEditRule = (rule: AlertRule) => {
    setEditingRule(rule);
    setShowRuleModal(true);
  };

  const handleCloseModal = () => {
    setShowRuleModal(false);
    setEditingRule(null);
  };

  const handleSubmitRule = async (formData: AlertRuleFormData) => {
    try {
      if (editingRule) {
        await updateAlertRule(editingRule.id, formData);
      } else {
        await createAlertRule(formData);
      }
      handleCloseModal();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SolarMonitoringHeader monitoringStatus={monitoringStatus} />
      
      <NavigationTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'overview' && (
        <OverviewTab 
          monitoringStatus={monitoringStatus} 
          alertHistory={alertHistory} 
        />
      )}

      {activeTab === 'rules' && (
        <AlertRulesTab
          alertRules={alertRules}
          onCreateRule={handleCreateRule}
          onEditRule={handleEditRule}
          onDeleteRule={deleteAlertRule}
          onToggleRule={toggleAlertRule}
        />
      )}

      {activeTab === 'history' && (
        <AlertHistoryTab alertHistory={alertHistory} />
      )}

      <AlertRuleModal
        isOpen={showRuleModal}
        editingRule={editingRule}
        onClose={handleCloseModal}
        onSubmit={handleSubmitRule}
        onTestEmail={testAlertEmail}
        testEmailLoading={testEmailLoading}
      />
    </div>
  );
};